// WaVerify — landing page for the WhatsApp magic link.
//
// When a user taps the link from WhatsApp we land here with ?token=...
// We POST it to /api/auth/wa/verify and either:
//   • requires_reactivation=true → show the Reactivation ConfirmSheet
//   • normal verify → session cookie set, navigate home
//
// Also handles phone-change verify links (same endpoint, same token).
import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { CheckCircle2, AlertCircle, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { useI18n } from "../lib/i18n";
import { migrateToAccount } from "../lib/guestStorage";

export default function WaVerify() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { lang } = useI18n();
  const [state, setState] = useState("verifying"); // verifying | reactivate | ok | error
  const [error, setError] = useState("");
  const [reactivation, setReactivation] = useState(null); // {token, user}
  const [busy, setBusy] = useState(false);
  const T = (en, es) => (lang === "es" ? es : en);

  useEffect(() => {
    const token = params.get("token");
    if (!token) { setState("error"); setError(T("Missing token", "Token faltante")); return; }
    (async () => {
      try {
        const { data } = await api.post("/auth/wa/verify", { token, language: lang });
        if (data.requires_reactivation) {
          setReactivation({ token: data.reactivation_token, user: data.user });
          setState("reactivate");
          return;
        }
        setState("ok");
        // Migrate guest localStorage data (saves, VIP, spend, custom locations) into the new account.
        try {
          const m = await migrateToAccount(api);
          if (m && m.migrated > 0) {
            toast.success(lang === "es" ? `Migrado: ${m.migrated} elementos` : `Migrated ${m.migrated} items`);
          }
        } catch { /* swallow — migration is best-effort */ }
        // Complete any pending action that triggered the verification flow.
        try {
          const raw = localStorage.getItem("habano.pending_action");
          if (raw) {
            const action = JSON.parse(raw);
            if (action.kind === "vip_request" && action.business_id) {
              await api.post(`/businesses/${action.business_id}/vip-reminder`).catch(() => {});
              toast.success(lang === "es"
                ? `Solicitud VIP enviada${action.business_name ? ` · ${action.business_name}` : ""}`
                : `VIP request sent${action.business_name ? ` · ${action.business_name}` : ""}`);
            }
            localStorage.removeItem("habano.pending_action");
          }
        } catch { /* ignore */ }
        toast.success(T("Signed in", "Sesión iniciada"));
        setTimeout(() => navigate("/journey"), 600);
      } catch (e) {
        setState("error");
        setError(e.response?.data?.detail || e.message);
      }
    })();
  }, [params, navigate, lang]);

  const confirmReactivate = async (confirm) => {
    if (!reactivation) return;
    setBusy(true);
    try {
      const { data } = await api.post("/auth/wa/reactivate", { reactivation_token: reactivation.token, confirm });
      if (confirm && data.reactivated) {
        // Migrate any guest data accrued before reactivation.
        try { await migrateToAccount(api); } catch { /* ignore */ }
        toast.success(T("Your account has been restored", "Tu cuenta ha sido restaurada"));
        navigate("/journey");
      } else {
        toast.message(T("Welcome back as a guest", "Bienvenido como invitado"));
        navigate("/");
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    } finally { setBusy(false); }
  };

  return (
    <main data-testid="wa-verify-page" className="min-h-screen grid place-items-center px-6" style={{ background: "#0B0B0B" }}>
      <div style={{ background: "#171717", border: "1px solid rgba(232,184,75,0.3)", borderRadius: 16, padding: 28, maxWidth: 420, width: "100%", textAlign: "center" }}>
        {state === "verifying" && (
          <>
            <Loader2 className="w-8 h-8 text-gold mx-auto animate-spin" strokeWidth={1.5}/>
            <h1 className="mt-4 text-foreground" style={{ fontFamily: "'Playfair Display', serif", fontSize: 22 }}>
              {T("Verifying…", "Verificando…")}
            </h1>
          </>
        )}
        {state === "reactivate" && reactivation && (
          <div data-testid="wa-reactivate-sheet">
            <h1 style={{ fontFamily: "'Playfair Display', serif", fontSize: 22, color: "#F5F1E8" }}>
              {T("Welcome back", "Bienvenido de vuelta")}
            </h1>
            <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 14, color: "#B0AAA3", lineHeight: 1.5, marginTop: 12 }}>
              {T(
                "Your account was scheduled for deletion. Would you like to reactivate it?",
                "Tu cuenta estaba programada para eliminación. ¿Te gustaría reactivarla?",
              )}
            </p>
            <button
              data-testid="wa-reactivate-yes"
              onClick={() => confirmReactivate(true)}
              disabled={busy}
              style={{ width: "100%", minHeight: 48, marginTop: 22, borderRadius: 10, border: "none", background: "linear-gradient(135deg, #d4b25a 0%, #E8B84B 100%)", color: "#0B0B0B", fontFamily: "'DM Sans', sans-serif", fontSize: 14, fontWeight: 700 }}
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin inline" strokeWidth={1.5}/> : T("Yes, reactivate my account", "Sí, reactivar mi cuenta")}
            </button>
            <button
              data-testid="wa-reactivate-no"
              onClick={() => confirmReactivate(false)}
              disabled={busy}
              style={{ width: "100%", minHeight: 48, marginTop: 10, borderRadius: 10, background: "transparent", border: "1.5px solid rgba(232,184,75,0.5)", color: "#E8B84B", fontFamily: "'DM Sans', sans-serif", fontSize: 14, fontWeight: 600 }}
            >
              {T("No thanks", "No, gracias")}
            </button>
          </div>
        )}
        {state === "ok" && (
          <>
            <CheckCircle2 className="w-10 h-10 text-emerald-400 mx-auto" strokeWidth={1.5}/>
            <h1 className="mt-4 text-foreground" style={{ fontFamily: "'Playfair Display', serif", fontSize: 22 }}>
              {T("Signed in", "Sesión iniciada")}
            </h1>
            <p className="mt-2 text-foreground/55 text-sm">{T("Redirecting…", "Redirigiendo…")}</p>
          </>
        )}
        {state === "error" && (
          <>
            <AlertCircle className="w-10 h-10 text-destructive mx-auto" strokeWidth={1.5}/>
            <h1 className="mt-4 text-foreground" style={{ fontFamily: "'Playfair Display', serif", fontSize: 22 }}>
              {T("Invalid link", "Enlace inválido")}
            </h1>
            <p className="mt-2 text-foreground/55 text-sm">{error}</p>
            <button onClick={() => navigate("/")} className="btn-outline mt-5 px-4 py-2 text-xs min-h-[44px]">
              {T("Back to home", "Volver al inicio")}
            </button>
          </>
        )}
      </div>
    </main>
  );
}
